import {
    isAfter,
    isBefore,
    endOfMonth,
    isSameMonth,
    eachWeekOfInterval,
    eachMonthOfInterval,
    eachDayOfInterval,
    areIntervalsOverlapping,
    endOfDay,
    startOfDay,
    isSameDay,
    isWithinInterval,
    endOfWeek,
    isSameWeek,
    getISODay,
    differenceInBusinessDays,
    startOfMonth,
    isMonday,
    isFriday,
    isWeekend,
    startOfWeek,
    addDays,
    format,
    min,
    max,
    nextFriday,
    subDays,
} from "date-fns"

import uniq from "lodash/uniq"
import { getTimeEntryHours, getTimesheetHours } from "../comp/DateUtils"
import numeral from "numeral"
import { toZonedTime } from "date-fns-tz"
import { makeRateLookup } from "./fxLookup"
import { getAllActionItems } from "./planTree"
import {
    BILLING,
    costPlus,
    getBillingLock,
    getProgressRevenue,
    getTaskBilling,
    isFixedFee,
    isRevenueCalculated,
} from "./packageBilling"

export function excludeNull(val) {
    return val === null ? undefined : val
}

function isValidNumber(v) {
    return v !== null && v !== undefined && v >= 0
}

const getTimesheetPlanItemId = (ts) => {
    return ts.planItemId || getObjectId(ts.snapshot?.planItem) || getObjectId(ts.planItem)
}

const makeHash = (array, key = "_id") => {
    const hash = {}

    if (!array) {
        return hash
    }

    for (let i = 0; i < array.length; i++) {
        hash[array[i][key]] = array[i]
    }

    return hash
}

const getAmountBasedOnRate = ({ planItem, rateToUse, timeToUse, from, to, fxm }) => {
    let amount = 0

    if (from && to && isAfter(from, to)) {
        return 0
    }

    if (!fxm) {
        fxm = () => 1
    }

    function getStartDateToUse(planItem, from) {
        const piStartDate = toZonedTime(planItem.startDate)

        if (!from) {
            return piStartDate
        }
        if (isAfter(from, piStartDate)) {
            return from
        }

        return piStartDate
    }
    function getEndDateToUse(planItem, to) {
        const piEndDate = toZonedTime(planItem.endDate)

        if (!to) {
            return piEndDate
        }
        if (isAfter(piEndDate, to)) {
            return to
        }

        return piEndDate
    }

    let startDateToUse = getStartDateToUse(planItem, from) //getStartDateToUsefrom && isBefore from || toZonedTime(planItem.startDate)
    let endDateToUse = getEndDateToUse(planItem, to)

    if (isAfter(startDateToUse, endDateToUse)) {
        return 0
    }

    let adjustedEndDate = startOfDay(addDays(endDateToUse, 1))

    const hourlyRates = getHourlyRates(planItem)

    const dayRate = ensureNumber(hourlyRates[rateToUse]) * 8

    const myRate = ensureNumber(planItem[rateToUse])

    if (planItem[timeToUse] === "One time") {
        // A one-time amount lands once, in the month of the end date, not in every
        // month the item spans (the server engine does the same).
        amount = isOneTimePlanItemInRangeCheck({ planItem, from, to })
            ? (planItem[rateToUse] || 0) * (planItem.quantity || 1)
            : 0
    } else {
        if (planItem[timeToUse] === "Yearly") {
            const bizDays = differenceInBusinessDays(adjustedEndDate, startDateToUse)
            amount = bizDays * 8 * hourlyRates[rateToUse]
        } else if (planItem[timeToUse] === "Monthly") {
            // A full month is worth the monthly rate. A partial month is worth the rate
            // prorated by the business days covered over the business days in that month.
            // (Business days times an 8h day rate could exceed the monthly rate whenever a
            // month has more than 21.67 working days.)
            let months = []

            try {
                months = eachMonthOfInterval({
                    start: startDateToUse,
                    end: endDateToUse,
                })
            } catch (e) {}

            months.forEach((month) => {
                const mStart = startOfMonth(month)
                const mEnd = endOfMonth(month)
                const rangeStart = max([mStart, startDateToUse])
                const rangeEnd = min([mEnd, endDateToUse])

                if (isAfter(rangeStart, rangeEnd)) {
                    return
                }

                const bizInMonth = differenceInBusinessDays(startOfDay(addDays(mEnd, 1)), mStart)
                const bizInRange = differenceInBusinessDays(startOfDay(addDays(rangeEnd, 1)), rangeStart)

                if (!bizInMonth) {
                    return
                }

                amount += bizInRange >= bizInMonth ? myRate : myRate * (bizInRange / bizInMonth)
            })
        } else if (planItem[timeToUse] === "Weekly") {
            let fullWeeks = eachWeekOfInterval(
                {
                    start: startDateToUse,
                    end: endDateToUse,
                },
                {
                    weekStartsOn: 1,
                },
            ).length

            if (!isMonday(startDateToUse)) {
                fullWeeks--
            }
            if (!isFriday(endDateToUse) && !isWeekend(endDateToUse)) {
                fullWeeks--
            }

            if (fullWeeks === 0 || fullWeeks < 0) {
                const bizDays = differenceInBusinessDays(adjustedEndDate, startDateToUse)
                amount = bizDays * dayRate
            } else {
                amount = fullWeeks * myRate

                if (!isMonday(startDateToUse)) {
                    //get partial month days at the start
                    const daysTillFullWeek = differenceInBusinessDays(
                        endOfWeek(startDateToUse, { weekStartsOn: 1 }),
                        startDateToUse,
                    )

                    amount += daysTillFullWeek * dayRate
                }
                if (!isFriday(endDateToUse) && !isWeekend(endDateToUse)) {
                    //get partial month days at the end
                    const daysAfterFullWeek = differenceInBusinessDays(
                        adjustedEndDate,
                        startOfWeek(endDateToUse, { weekStartsOn: 1 }),
                    )

                    amount += daysAfterFullWeek * dayRate
                }
            }
        } else if (planItem[timeToUse] === "Daily") {
            const bizDays = differenceInBusinessDays(adjustedEndDate, startDateToUse)
            amount = bizDays * myRate
        } else if (planItem[timeToUse] === "Hourly") {
            const bizzDays = differenceInBusinessDays(adjustedEndDate, startDateToUse)

            amount = bizzDays * myRate * 8
        }
    }

    return amount * fxm(endDateToUse)
}

function getObjectId(id, handlePopulatedRef = true) {
    if (!id) {
        return id
    }

    if (typeof id === "string") {
        return id
    }

    if (id._id && handlePopulatedRef === true) {
        // Populated reference. Re-iterate once with child _id
        return getObjectId(id._id, false)
    }

    return id
}

// ignoreFixed: the task's own amounts even where a package fixes them by hand (what the budget
// sheet shows as not counted)
export const getRaciBudgetForRole = ({
    mission,
    planItem,
    timesheets = [],
    from,
    to,
    fxm,
    hoursOnly,
    forTask,
    ignoreFixed,
}) => {
    let totals = {
        hoursNeeded: 0,
        estCost: 0,
        estRevenue: 0,
        actualCost: 0,
        actualRevenue: 0,
        actualHours: 0,
    }

    if (!fxm) {
        fxm = () => {
            return 1
        }
    }

    const isBillable = isMissionBillable(mission)

    const allA = getAllActions(mission)
    const allAis = forTask ? [forTask] : getAllActionItems(mission)
    const allAi = makeHash(allAis)
    // Work packages billed their own way (packageBilling.js); most missions have none
    const billsByPackage = hasPackageBilling(allA)

    const rt = planItem.raciOnTasks

    const raciTaskIds = uniq(planItem.raciOnTasks?.map((r) => r.taskId))

    if (rt?.length) {
        rt.forEach((r, i) => {
            const hrlyRate = hoursOnly ? {} : getHourlyRates(planItem, r)

            const ai = allAi[r.taskId]

            if (!ai) {
                return
            }

            // Amounts fixed by hand on its package, or any package above it, replace the task's own
            const parentCosts = ignoreFixed
                ? { ...NOTHING_FIXED }
                : getAmountsSetForLinkedTask({ ai, allActions: allA, isBillable })

            const hn = getHoursNeedFromRaci(r, ai, from, to)

            // Cost plus bills the cost plus the markup, so its revenue follows the cost
            const markup = billsByPackage ? getCostPlusMarkup({ ai, allActions: allA }) : null

            if (!hoursOnly) {
                if (!parentCosts.estCost) {
                    if (r.rateTime === "One time" && from && to) {
                        totals.estCost += isWithinInterval(toZonedTime(ai.endDate), { start: from, end: to })
                            ? ensureNumber(hrlyRate.rate) * fxm(from)
                            : 0
                    } else {
                        totals.estCost +=
                            ensureNumber(hn) * ensureNumber(hrlyRate.rate) * fxm(from || toZonedTime(ai.endDate))
                    }
                }

                if (isBillable && !parentCosts.estRevenue && markup !== null) {
                    if (r.rateTime === "One time" && from && to) {
                        totals.estRevenue += isWithinInterval(toZonedTime(ai.endDate), { start: from, end: to })
                            ? costPlus(hrlyRate.rate, markup) * fxm(from)
                            : 0
                    } else {
                        totals.estRevenue +=
                            costPlus(ensureNumber(hn) * ensureNumber(hrlyRate.rate), markup) *
                            fxm(from || toZonedTime(ai?.endDate))
                    }
                } else if (isBillable && !parentCosts.estRevenue) {
                    if (r.billUnit === "One time" && from && to) {
                        totals.estRevenue += isWithinInterval(toZonedTime(ai.endDate), { start: from, end: to })
                            ? ensureNumber(hrlyRate.billRate) * fxm(from)
                            : 0
                    } else {
                        totals.estRevenue +=
                            ensureNumber(hn) * ensureNumber(hrlyRate.billRate) * fxm(from || toZonedTime(ai?.endDate))
                    }
                }
            }

            totals.hoursNeeded += ensureNumber(hn)

            let myTsTaskHours = 0
            // What those hours cost and bill at the rate they were approved at: the timesheet's
            // copy of the role (its snapshot), as Budget pro prices them - not today's rate
            let myTsTaskCost = 0
            let myTsTaskRevenue = 0
            const addTsHours = (ts, tsa, hours) => {
                const then = ts.snapshot?.planItem || planItem
                const rates = getHourlyRates(
                    then,
                    then?.raciOnTasks?.find((rt) => rt.taskId === tsa.actionItemId),
                )
                myTsTaskHours += hours
                myTsTaskCost += hours * ensureNumber(rates.rate)
                myTsTaskRevenue += hours * ensureNumber(rates.billRate)
            }

            if (!r.actualHours && !hoursOnly) {
                const mySheets = timesheets.filter((t) => {
                    if (from && to) {
                        if (
                            !areIntervalsOverlapping(
                                {
                                    start: toZonedTime(t.weekStart),
                                    end: toZonedTime(t.weekEnd),
                                },
                                {
                                    start: from,
                                    end: to,
                                },
                            )
                        ) {
                            return false
                        }
                    }

                    return getTimesheetPlanItemId(t) === planItem._id && t.status === "approved"
                })

                if (from && to) {
                    mySheets.forEach((ts, i) => {
                        let startIndex = 0
                        let endIndex = 6

                        if (isSameWeek(toZonedTime(ts.weekStart), from, { weekStartsOn: 1 })) {
                            startIndex = getISODay(from) - 1
                        }
                        if (isSameWeek(toZonedTime(ts.weekEnd), to, { weekStartsOn: 1 })) {
                            endIndex = getISODay(to) - 1
                        }
                        ts.actionItems.forEach((tsa, i) => {
                            if (forTask && tsa.actionItemId !== forTask._id) {
                                return
                            }

                            if (!raciTaskIds.includes(tsa.actionItemId)) {
                                return
                            }

                            if (tsa.actionItemId !== ai._id) {
                                return
                            }

                            addTsHours(ts, tsa, ensureNumber(getTimeEntryHours(tsa, startIndex, endIndex)))
                        })
                    })
                } else {
                    const mySheets = timesheets.filter(
                        (t) => getTimesheetPlanItemId(t) === planItem._id && t.status === "approved",
                    )

                    mySheets.forEach((ts, i) => {
                        ts.actionItems.forEach((a, i) => {
                            if (forTask && a.actionItemId !== forTask._id) {
                                return
                            }

                            if (!raciTaskIds.includes(a.actionItemId)) {
                                return
                            }

                            if (a.actionItemId !== ai._id) {
                                return
                            }

                            addTsHours(
                                ts,
                                a,
                                a.days.split(",").reduce((cumm, obj) => (cumm += ensureNumber(obj)), 0),
                            )
                        })
                    })
                }
            }

            if (!hoursOnly) {
                // start at 0 and compute the hours that actually fall in this window
                let actualHoursForWindow = 0

                if (r.actualHours && from && to) {
                    const actionStart = toZonedTime(ai.startDate)
                    const actionEnd = toZonedTime(ai.endDate)
                    const allBusinessDays = eachDayOfInterval({ start: actionStart, end: actionEnd }).filter(
                        (d) => !isWeekend(d),
                    )

                    if (allBusinessDays.length > 0) {
                        const maxDailyHours = 8 // adjust if needed
                        let remainingHours = ensureNumber(r.actualHours)

                        // normalize window boundaries to day boundaries to avoid time issues
                        const windowStart = startOfDay(from)
                        const windowEnd = endOfDay(to)

                        for (let day of allBusinessDays) {
                            if (remainingHours <= 0) break

                            // hours to allocate for this day (never exceed remainingHours)
                            const h = Math.min(maxDailyHours, remainingHours)

                            // only add if this day falls inside the current window
                            if (!isBefore(day, windowStart) && !isAfter(day, windowEnd)) {
                                actualHoursForWindow += h
                            }

                            // subtract the hours allocated for this day from remaining pool
                            remainingHours -= h
                        }
                    }
                } else if (!r.actualHours) {
                    // fallback to timesheet computed hours (unchanged)
                    actualHoursForWindow = myTsTaskHours
                } else if (r.actualHours) {
                    actualHoursForWindow = r.actualHours
                }

                totals.actualHours += actualHoursForWindow

                // Logged hours at the rate they were approved at; hours typed by hand on the
                // staffing (r.actualHours) have no timesheet, so today's rate
                const fromSheets = !r.actualHours

                if (!parentCosts.actualCost && r.rateTime !== "One time") {
                    const fxdate = to || toZonedTime(ai.endDate)
                    const fxe = fxm(fxdate)

                    totals.actualCost +=
                        (fromSheets ? myTsTaskCost : ensureNumber(actualHoursForWindow) * ensureNumber(hrlyRate.rate)) *
                        fxe
                }
                if (isBillable && !parentCosts.actualRevenue && markup !== null && r.rateTime !== "One time") {
                    totals.actualRevenue +=
                        costPlus(
                            fromSheets
                                ? myTsTaskCost
                                : ensureNumber(actualHoursForWindow) * ensureNumber(hrlyRate.rate),
                            markup,
                        ) * fxm(to || toZonedTime(ai.endDate))
                } else if (isBillable && !parentCosts.actualRevenue && markup === null && r.billUnit !== "One time") {
                    totals.actualRevenue +=
                        (fromSheets
                            ? myTsTaskRevenue
                            : ensureNumber(actualHoursForWindow) * ensureNumber(hrlyRate.billRate)) *
                        fxm(to || toZonedTime(ai.endDate))
                }
            }
        })
    }

    if (!forTask) {
        timesheets
            ?.filter((t) => {
                if (t.status !== "approved") return false
                if (getTimesheetPlanItemId(t) !== planItem?._id) return false

                if (from && to) {
                    const isin = areIntervalsOverlapping(
                        {
                            start: toZonedTime(t.weekStart),
                            end: toZonedTime(t.weekEnd),
                        },
                        {
                            start: from,
                            end: to,
                        },
                    )

                    return isin
                }

                return true
            })
            .forEach((ts, i) => {
                const newTs = {
                    ...ts,
                    actionItems: ts.actionItems?.filter((tsa) => !raciTaskIds.includes(tsa.actionItemId)),
                }

                let startIndex = 0
                let endIndex = 6

                if (from && to) {
                    if (isSameWeek(toZonedTime(ts.weekStart), from, { weekStartsOn: 1 })) {
                        startIndex = getISODay(from) - 1
                    }
                    if (isSameWeek(toZonedTime(ts.weekEnd), to, { weekStartsOn: 1 })) {
                        endIndex = getISODay(to) - 1
                    }
                }

                const hr = getHourlyRates(
                    ts.snapshot?.planItem ||
                        ts.planItem ||
                        mission?.planItems?.find((p) => p._id === getTimesheetPlanItemId(ts)),
                )

                const fxe = fxm(to || toZonedTime(ts.weekEnd))
                const hrs = getTimesheetHours(newTs, startIndex, endIndex)
                totals.actualHours += ensureNumber(hrs)
                totals.actualCost += ensureNumber(hrs * hr.rate * fxe)

                if (isBillable && billsByPackage) {
                    // Each entry bills the way its task's package does
                    newTs.actionItems?.forEach((tsa) => {
                        const h = ensureNumber(getTimeEntryHours(tsa, startIndex, endIndex))
                        const task = allAi[tsa.actionItemId]

                        if (isUnderFixedFee({ ai: task, allActions: allA })) return

                        const markup = getCostPlusMarkup({ ai: task, allActions: allA })

                        totals.actualRevenue += ensureNumber(
                            (markup === null ? h * hr.billRate : costPlus(h * hr.rate, markup)) * fxe,
                        )
                    })
                } else if (isBillable) {
                    totals.actualRevenue += ensureNumber(hrs * hr.billRate * fxe)
                }
            })
    }

    return totals
}

function isWithinOptionalInterval(date, start, end) {
    // If both boundaries are missing, everything is "within"
    if (!start && !end) return true

    // If we only have a start, require date >= start
    if (start && !end) {
        return date.getTime() >= start.getTime()
    }

    // If we only have an end, require date <= end
    if (!start && end) {
        return date.getTime() <= end.getTime()
    }

    // If both boundaries exist, use date-fns isWithinInterval
    return isWithinInterval(date, { start, end })
}

export function distributeHoursExcludingWeekendsIsoWeekly(
    distributionStart,
    distributionEnd,
    totalHours,
    filterStart,
    filterEnd,
) {
    // Utility function to get the earlier of two dates
    const getEarlierDate = (date1, date2) => (date1 < date2 ? date1 : date2)
    const getLaterDate = (date1, date2) => (date1 > date2 ? date1 : date2)

    // 1. Get all ISO weeks in [distributionStart, distributionEnd]
    //    ISO week means Monday is the start of the week.
    const weeks = eachWeekOfInterval(
        {
            start: distributionStart,
            end: distributionEnd,
        },
        { weekStartsOn: 1 }, // ISO weeks start on Monday
    )

    // 2. For each week, determine how many weekdays (Mon-Fri) it has
    //    within [weekStart, endOfWeek(weekStart)] but capped by distributionStart and distributionEnd
    const weeklyWorkdays = weeks.map((weekStart) => {
        const effectiveStart = getLaterDate(weekStart, distributionStart)
        const weekEnd = getEarlierDate(endOfWeek(weekStart, { weekStartsOn: 1 }), distributionEnd)

        // Count only weekdays (Mon=1..Fri=5); getDay() => 1..5
        let count = 0
        for (let day = effectiveStart; day <= weekEnd; day = addDays(day, 1)) {
            const dayOfWeek = day.getDay()
            if (dayOfWeek !== 0 && dayOfWeek !== 6) {
                count++
            }
        }

        return { weekStart, count }
    })

    // 3. Sum total workdays across all ISO weeks
    const totalWorkdays = weeklyWorkdays.reduce((sum, { count }) => sum + count, 0)

    // If there are no valid workdays, return empty
    if (totalWorkdays === 0) {
        return []
    }

    // 4. Distribute totalHours across all workdays
    const baseHours = Math.floor(totalHours / totalWorkdays)
    const leftover = totalHours % totalWorkdays

    // 5. Build a weekly distribution array with baseHours * numberOfWorkdays
    let weeklyDistribution = weeklyWorkdays.map(({ weekStart, count }) => ({
        weekStart,
        hours: baseHours * count, // base allotment for that week's count of workdays
    }))

    // 6. Distribute leftover hours from the end backward
    let remainingLeftover = leftover
    for (let i = weeklyDistribution.length - 1; i >= 0 && remainingLeftover > 0; i--) {
        const { count } = weeklyWorkdays[i]
        const extraHours = Math.min(remainingLeftover, count)
        weeklyDistribution[i].hours += extraHours
        remainingLeftover -= extraHours
    }

    // 7. Filter out distributions if their weekStart is outside [filterStart, filterEnd]
    //    (assuming entire weeks should be filtered in or out)
    weeklyDistribution = weeklyDistribution.filter(({ weekStart }) =>
        isWithinOptionalInterval(weekStart, filterStart, filterEnd),
    )

    return weeklyDistribution
}

export function distributeHoursExcludingWeekends(start, end, totalHours, from, to) {
    // Normalize to midnight

    // 1. Collect all weekdays in full range
    const workdays = []
    let current = new Date(start)

    while (current <= end) {
        const day = current.getDay()
        if (day !== 0 && day !== 6) {
            workdays.push(new Date(current))
        }
        current.setDate(current.getDate() + 1)
    }

    const totalWorkdays = workdays.length
    if (totalWorkdays === 0) return []

    // 2. Distribute totalHours
    const intHours = Math.floor(totalHours)
    const baseHours = Math.floor(intHours / totalWorkdays)
    const leftover = intHours % totalWorkdays
    const decimalPart = totalHours - intHours

    const distribution = new Array(totalWorkdays).fill(baseHours)
    for (let i = totalWorkdays - leftover; i < totalWorkdays; i++) {
        distribution[i] += 1
    }
    if (decimalPart > 0 && distribution.length > 0) {
        distribution[distribution.length - 1] += decimalPart
    }

    // 3. Build full result
    const result = workdays.map((date, i) => ({
        date,
        hours: distribution[i],
    }))

    if (from && to) {
        return result.filter((r) => {
            return isWithinInterval(r.date, { start: from, end: to })
        })
    } else {
        return result
    }
}

export function getRoleItemCostAndRevenue({
    planItem,
    from,
    to,
    mission,
    roleTimesheets = [],
    fxm,
    forceCalculate,
    slim,
}) {
    if (!fxm) {
        fxm = (d) => {
            return 1
        }
    }
    const data = {
        actualHours: 0,
        actualCost: 0,
        estCost: 0,
        estRevenue: 0,
        actualRevenue: 0,
        estHours: 0,
    }

    if (!mission || !planItem || planItem.createdFromGtCrm) {
        return data
    }

    let inRangeTimesheets =
        from && to
            ? roleTimesheets.filter((ts) => {
                  return areIntervalsOverlapping(
                      {
                          start: from,
                          end: to,
                      },
                      {
                          start: toZonedTime(ts.weekStart),
                          end: toZonedTime(ts.weekEnd),
                      },
                  )
              })
            : roleTimesheets

    // Hours on a task whose package bills cost plus earn their cost plus the markup
    const allActions = inRangeTimesheets.length ? getAllActions(mission) : []
    const tasksById = hasPackageBilling(allActions) ? makeHash(getAllActionItems(mission)) : null

    inRangeTimesheets.forEach((ts) => {
        ts.actionItems.forEach((tsa, i) => {
            let startIndex = 0
            let endIndex = 6

            if (from && to) {
                if (isSameWeek(toZonedTime(ts.weekStart), from, { weekStartsOn: 1 })) {
                    startIndex = getISODay(from) - 1
                }
                if (isSameWeek(toZonedTime(ts.weekEnd), to, { weekStartsOn: 1 })) {
                    endIndex = getISODay(to) - 1
                }
            }

            let myHoursHere = ensureNumber(getTimeEntryHours(tsa, startIndex, endIndex))

            const planItemObj = ts.snapshot?.planItem || planItem
            const raciObj = planItemObj?.raciOnTasks?.find((rt) => rt.taskId === tsa.actionItemId)

            let tsRates = getHourlyRates(planItemObj, raciObj)

            const f = subDays(toZonedTime(ts.weekEnd), 3)

            data.actualHours += myHoursHere
            data.actualCost += myHoursHere * tsRates.rate * fxm(f)

            const task = tasksById?.[tsa.actionItemId]
            const markup = tasksById ? getCostPlusMarkup({ ai: task, allActions }) : null

            // Under a fixed fee the fee is the revenue, not the hours
            if (tasksById && isUnderFixedFee({ ai: task, allActions })) return

            data.actualRevenue +=
                markup === null
                    ? ensureNumber(myHoursHere * tsRates.billRate * fxm(f))
                    : ensureNumber(costPlus(myHoursHere * tsRates.rate, markup) * fxm(f))
        })
    })

    if (from && to) {
        if (
            !areIntervalsOverlapping(
                {
                    start: from,
                    end: to,
                },
                {
                    start: toZonedTime(planItem.startDate),
                    end: toZonedTime(planItem.endDate),
                },
            )
        ) {
            return data
        }
    }

    if (!fxm) {
        fxm = () => 1
    }

    let hourlyRates = getHourlyRates(planItem)

    if (from && to && isAfter(from, to)) {
        return data
    }

    const pStart = toZonedTime(planItem.startDate)
    const pEnd = toZonedTime(planItem.endDate)

    const starter = from || pStart
    const ender = to || pEnd

    if (mission.isProcess || mission.projectType === "mx-gantt") {
        const b = getRaciBudgetForRole({
            mission,
            planItem,
            timesheets: [],
            from,
            to,
            fxm,
        })

        data.estHours += b.hoursNeeded
        data.estCost += b.estCost
        data.estRevenue += b.estRevenue

        return data
    }

    //Continue logic if not gantt

    if (planItem.hoursNeeded === 0 && planItem.hoursNeeded !== null) {
        return data
    } else if (planItem.hoursNeeded > 0 && planItem.rateTime !== "One time") {
        const dailyDistribution = distributeHoursExcludingWeekends(
            toZonedTime(planItem.startDate),
            toZonedTime(planItem.endDate),
            planItem.hoursNeeded,
            from,
            to,
        )

        dailyDistribution.forEach((obj, i) => {
            data.estCost += ensureNumber(obj.hours * hourlyRates.rate * fxm(toZonedTime(planItem.endDate)))

            data.estRevenue +=
                mission.notBillable && !forceCalculate
                    ? 0
                    : ensureNumber(obj.hours * hourlyRates.billRate * fxm(toZonedTime(planItem.endDate)))
        })

        return data
    } else if (planItem.hoursNeeded > 0 && planItem.rateTime === "One time") {
        // One-time role: the flat amount belongs to the end-date month only
        if (!isOneTimePlanItemInRangeCheck({ planItem, from, to })) {
            return data
        }
        data.estCost = ensureNumber(planItem.rate * fxm(toZonedTime(planItem.endDate)))
        data.estRevenue =
            mission.notBillable && !forceCalculate
                ? 0
                : ensureNumber(planItem.hoursNeeded * hourlyRates.billRate * fxm(toZonedTime(planItem.endDate)))
        data.estHours = ensureNumber(planItem.hoursNeeded)

        return data
    } else {
        let isInRange = isRecurringPlanItemInRangeCheck({ planItem, from, to, mission })

        if (isInRange) {
            //Main logic
            let cost = 0
            let bill = 0

            //Do cost
            cost = getAmountBasedOnRate({ planItem, rateToUse: "rate", timeToUse: "rateTime", from, to, fxm })

            bill = getAmountBasedOnRate({ planItem, rateToUse: "billRate", timeToUse: "billUnit", from, to, fxm })

            //const subMe = data.conflictingHours ? getHourlyRates(planItem).billRate * data.conflictingHours : 0

            data.estCost = ensureNumber(cost)
            data.estRevenue = ensureNumber(bill) //mission.billUsingMilestones ? 0 : bill - subMe) //For stupid tasks that are fixed bid

            data.estHours =
                eachDayOfInterval({
                    start: starter,
                    end: ender,
                }).filter((d) => !isWeekend(d)).length * 8

            return data
        } else {
            return data
        }
    }
}

export function getMonthKey(year, i) {
    const std = new Date(year, i, 1)
    return format(std, "yyyy-MM-dd")
}

export function isRecurringPlanItemInRangeCheck({ planItem, from, to, mission }) {
    if (!from && !to) {
        return true
    }

    if (!planItem.startDate || !planItem.endDate) {
        return false //Bad data
    }

    if (!from) from = toZonedTime(planItem.startDate)
    if (!to) to = toZonedTime(planItem.endDate)

    let isIn = false

    const utStart = toZonedTime(planItem.startDate)
    const utEnd = endOfDay(toZonedTime(planItem.endDate))

    try {
        isIn = areIntervalsOverlapping(
            {
                start: from,
                end: to,
            },
            {
                start: utStart,
                end: utEnd,
            },
        )
    } catch (e) {}

    return isIn
}

export function ensureNumber(val) {
    let newVal = val
    if (newVal === null) return 0
    if (newVal === undefined) return 0
    if (typeof newVal === "string") {
        newVal = newVal.replaceAll(",", "")
    }
    if (Number.isNaN(+newVal)) return 0
    return +newVal
}

export function isMissionBillable(mission) {
    if (!mission) {
        return false
    }
    if (mission?.notBillable) {
        return false
    }

    if (mission?.isModel) {
        return true
    }
    if (mission.org && mission.isTemplate) {
        return true
    }

    return Boolean(mission.client)
}

export function getHourlyRates(planItem, raciObj) {
    if (!planItem) {
        return { billRate: 0, rate: 0 }
    }

    const rate = isValidNumber(raciObj?.rate) ? raciObj.rate : ensureNumber(planItem.rate)
    const billRate = isValidNumber(raciObj?.billRate) ? raciObj?.billRate : ensureNumber(planItem.billRate)

    const rateTime = raciObj?.rateTime || planItem.rateTime
    const billUnit = raciObj?.billUnit || planItem.billUnit

    const factor = {
        Hourly: { rate: 1, billRate: 1 },
        Weekly: { rate: 1 / 40, billRate: 1 / 40 },
        Monthly: { rate: (1 / 40) * (12 / 52), billRate: (1 / 40) * (12 / 52) },
        Daily: { rate: 1 / 8, billRate: 1 / 8 },
        Yearly: { rate: (1 / 40) * (1 / 52), billRate: (1 / 40) * (1 / 52) },
    }

    const rateFactor = factor[rateTime]?.rate ?? 0
    const billRateFactor = factor[billUnit]?.billRate ?? 0

    const myRate = rateTime === "One time" ? rate : rate * rateFactor
    const myBillRate = billUnit === "One time" ? billRate : billRate * billRateFactor

    return {
        billRate: ensureNumber(numeral(myBillRate).format("0.00")),
        rate: ensureNumber(numeral(myRate).format("0.00")),
    }
}

// Lives in utils/planTree (a leaf module, so no import cycles); kept here for existing imports
export { getAllActionItems }
function getAllActions(mission) {
    if (!mission?.planItems) {
        return []
    }
    const out = []
    for (const p of mission.planItems) {
        if (p?.actions?.length) out.push(...p.actions)
    }
    return out
}

const MONTH_KEY = /^\d{4}-\d{2}$/

/**
 * Amounts typed month by month on a recurring cost line (planItem.monthly, entries
 * { month: "yyyy-MM", cost, bill }): each replaces that month's rate × quantity, on its
 * own side. One-time lines (one amount already) and equipment lines (priced from
 * bookings) don't take them. API twin: financials.engine.js#monthlyAmounts.
 *
 * @returns {{ cost: Map<string, number>, bill: Map<string, number> }}
 */
export function getMonthlyAmounts(planItem) {
    const cost = new Map()
    const bill = new Map()

    if (planItem?.type !== "cost" || planItem.equipmentType || planItem.rateTime === "One time") {
        return { cost, bill }
    }

    for (const m of planItem.monthly || []) {
        if (!MONTH_KEY.test(m?.month || "")) continue
        if (isTypedAmount(m.cost)) cost.set(m.month, +m.cost)
        if (isTypedAmount(m.bill)) bill.set(m.month, +m.bill)
    }

    return { cost, bill }
}

// Any finite number, credits included - the API's rule (missionPlan.utils.js#cleanMonthly)
function isTypedAmount(v) {
    return v !== null && v !== undefined && v !== "" && Number.isFinite(Number(v))
}

const getIntervalOfData = ({ planItem, mission, from, to, fxm }) => {
    let data = {
        estCost: 0,
        estRevenue: 0,
        actualCost: 0,
        actualRevenue: 0,
    }

    if (!fxm) {
        fxm = () => 1
    }

    const piStartDate = toZonedTime(planItem.startDate)
    const piEndDate = toZonedTime(planItem.endDate)

    const methodToUse =
        planItem.rateTime === "Monthly"
            ? eachMonthOfInterval
            : planItem.rateTime === "Weekly"
              ? eachWeekOfInterval
              : eachDayOfInterval

    const isInRange = isRecurringPlanItemInRangeCheck({ planItem, from, to, mission })

    if (isInRange) {
        const startDateToUse = !from
            ? piStartDate
            : isAfter(piStartDate, from) || isSameMonth(piStartDate, from)
              ? piStartDate
              : from
        const endDateToUse = !to ? piEndDate : isAfter(to, piEndDate) || isSameMonth(piEndDate, to) ? piEndDate : to

        let estC = 0
        let estR = 0
        let acC = 0
        let acR = 0

        let iterations

        if (planItem.rateTime === "Weekly") {
            // Each week belongs to exactly one month: the month holding its Monday, or the
            // item's start date when the item begins mid-week. Iterating weeks over the
            // clamped window instead would count a week that straddles a month boundary in
            // both months.
            iterations = eachWeekOfInterval(
                {
                    start: piStartDate,
                    end: piEndDate,
                },
                { weekStartsOn: 1 },
            )
                .map((monday) => max([monday, piStartDate]))
                .filter((weekDate) => isWithinOptionalInterval(weekDate, from, to))
        } else {
            iterations = methodToUse(
                {
                    start: startDateToUse,
                    end: endDateToUse,
                },
                { weekStartsOn: 1 },
            )
        }

        // An hourly line (equipment) is priced per calendar day at the mission's hours per day
        const hoursPerPeriod = planItem.rateTime === "Hourly" ? ensureNumber(mission?.hoursPerDay) || 8 : 1

        const typed = getMonthlyAmounts(planItem)
        const hasTyped = typed.cost.size > 0 || typed.bill.size > 0

        iterations.forEach((time, i) => {
            // A typed month replaces every period of that month, on its own side
            const key = hasTyped ? format(time, "yyyy-MM") : null
            const qty = (planItem.quantity || 1) * hoursPerPeriod
            const c = typed.cost.has(key) ? 0 : ensureNumber(planItem.rate) * qty * fxm(time)
            const r = typed.bill.has(key) ? 0 : ensureNumber(planItem.billRate) * qty * fxm(time)

            estC += c
            estR += r

            if (isBefore(time, endOfDay(new Date()))) {
                acC += c
                acR += r
            }
        })

        // The typed months the line and the window cover (months outside the line's dates
        // are kept on it but don't count). Like a monthly fee: actual once the month starts.
        if (hasTyped && !isAfter(startDateToUse, endDateToUse)) {
            eachMonthOfInterval({ start: startDateToUse, end: endDateToUse }).forEach((monthStart) => {
                const key = format(monthStart, "yyyy-MM")
                const at = max([monthStart, piStartDate])
                const isActual = isBefore(at, endOfDay(new Date()))

                if (typed.cost.has(key)) {
                    const c = typed.cost.get(key) * fxm(at)
                    estC += c
                    if (isActual) acC += c
                }
                if (typed.bill.has(key)) {
                    const r = typed.bill.get(key) * fxm(at)
                    estR += r
                    if (isActual) acR += r
                }
            })
        }

        //stored actuals are in mission currency - convert them like getLineItemCostAndRevenue does
        const fxEnd = fxm(piEndDate)

        // A typed actual is one figure for the whole item. It is recognised once, in the
        // month of the item's end date (as one-time items already are), not repeated in
        // every month the item spans.
        const overrideInRange = isOneTimePlanItemInRangeCheck({ planItem, from, to })

        return {
            estCost: ensureNumber(estC),
            estRevenue: ensureNumber(estR),
            actualCost: ensureNumber(
                isValidNumber(planItem.actualCost) ? (overrideInRange ? planItem.actualCost * fxEnd : 0) : acC,
            ),
            actualRevenue: ensureNumber(
                isValidNumber(planItem.actualRevenue) ? (overrideInRange ? planItem.actualRevenue * fxEnd : 0) : acR,
            ),
        }
    } else {
        return data
    }
}

export function isOneTimePlanItemInRangeCheck({ planItem, from, to }) {
    if (!from && !to) {
        return true
    }

    if (!planItem.endDate) {
        return false //Bad data
    }

    const ed = toZonedTime(planItem.endDate)

    //from/to arrive as local Dates already - do not run them through toZonedTime again
    if (from && isBefore(ed, startOfDay(from))) {
        return false
    }

    if (to && isAfter(ed, endOfDay(to))) {
        return false
    }

    return true
}

function traverseParents(actions, startActionId, callback) {
    let currentAction = actions.find((a) => a?._id === startActionId)
    // A package can't be its own ancestor, but bad data mustn't loop forever
    const seen = new Set([startActionId])

    while (currentAction?.parentId && !seen.has(currentAction.parentId)) {
        const parentId = currentAction.parentId
        seen.add(parentId)
        const parentAction = actions.find((a) => a?._id === parentId)
        if (!parentAction) break // stop if parent not found

        callback(parentAction) // run your logic on the parent
        currentAction = parentAction // move up
    }
}

const isSetAmount = (v) => v >= 0 && v !== null

/**
 * Whether a revenue typed on the package counts: only with no model (an override) or by %
 * complete (the fee). Time, cost plus and a lump sum calculate it (the work, the milestones):
 * a typed one is deleted on write and ignored here. API twin: financials.engine.js#hasHandTypedRevenue.
 */
export function hasHandTypedRevenue(action) {
    return !isRevenueCalculated(action?.billingType)
}

const getAmountsSetOn = (action, isBillable) => {
    // A fixed fee stands for all the revenue under it, the fee set yet or not
    const fixedFee = Boolean(isBillable) && isFixedFee(action.billingType)

    return {
        estCost: isSetAmount(action.estCost),
        actualCost: isSetAmount(action.actualCost),
        estRevenue: fixedFee || (Boolean(isBillable) && hasHandTypedRevenue(action) && isSetAmount(action.estRevenue)),
        actualRevenue:
            fixedFee || (Boolean(isBillable) && hasHandTypedRevenue(action) && isSetAmount(action.actualRevenue)),
    }
}

/** Whether any package of the mission bills its own way; most don't, and skip the lookups. */
const hasPackageBilling = (allActions) => Boolean(allActions?.some((a) => a?.billingType))

/**
 * The markup when a task's work package bills cost plus (packageBilling.js), else null.
 * API twin: financials.engine.js#costPlusMarkup.
 */
export function getCostPlusMarkup({ ai, allActions }) {
    if (!ai?.actionId || !hasPackageBilling(allActions)) return null

    const billing = getTaskBilling({ ai, allActions })

    return billing?.type === BILLING.COST_PLUS ? billing.markup : null
}

/**
 * Whether a task's work package bills a fixed fee (packageBilling.js): the fee is the
 * revenue, so its time, lines and milestones earn none of their own. API twin:
 * financials.engine.js.
 */
export function isUnderFixedFee({ ai, allActions }) {
    if (!ai?.actionId || !hasPackageBilling(allActions)) return false

    return isFixedFee(getTaskBilling({ ai, allActions })?.type)
}

const NOTHING_FIXED = { estCost: false, actualCost: false, estRevenue: false, actualRevenue: false }

/**
 * Which amounts a package (action) fixes by hand over what sits under it. A package with its
 * own amount - 0 included - stands for everything under it: its tasks, the roles and lines on
 * them, and the packages inside it (as the Gantt rolls packages up, MxGantt#aggregateNodeField).
 * So an amount fixed on ANY package up the chain replaces it, the nearest as much as the
 * topmost. (Only the topmost used to count: a fixed amount on a package inside another was
 * added on top of its own tasks.)
 *
 * @param includeSelf the package itself counts too: a task's own package, not a package's own amount
 */
function getAmountsFixedAbove({ allActions, actionId, isBillable, includeSelf }) {
    const fixed = { ...NOTHING_FIXED }
    const add = (action) => {
        const on = getAmountsSetOn(action, isBillable)
        Object.keys(fixed).forEach((k) => (fixed[k] = fixed[k] || on[k]))
    }
    const start = allActions.find((a) => a?._id === actionId)
    if (!start) return fixed

    if (includeSelf) add(start)
    traverseParents(allActions, actionId, add)

    return fixed
}

/**
 * Which amounts a package above this one fixes by hand: its own amount then gives way to
 * that one (missionFinancials, the budget sheets).
 */
export function getAmountsSetAbove({ allActions, actionId, isBillable }) {
    return getAmountsFixedAbove({ allActions, actionId, isBillable, includeSelf: false })
}

/**
 * Which amounts replace a task's own (its roles, the lines linked to it): fixed by hand on
 * its package or on any package above that.
 */
export function getAmountsSetForLinkedTask({ ai, allActions, isBillable }) {
    if (!ai?.actionId) return { ...NOTHING_FIXED }
    return getAmountsFixedAbove({ allActions, actionId: ai.actionId, isBillable, includeSelf: true })
}

/**
 * A line item's cost and revenue, in a period or over its life. On a work package billed
 * cost plus (packageBilling.js) the line bills its cost plus the markup, whatever its bill
 * rate. API twin: financials.engine.js#addLineItems.
 */
export function getLineItemCostAndRevenue(props) {
    const data = priceLineItem(props)
    const { planItem, mission } = props

    if (planItem?.type !== "cost" || !planItem.linkedToTask || !isMissionBillable(mission)) {
        return data
    }

    const allActions = getAllActions(mission)

    if (!hasPackageBilling(allActions)) {
        return data
    }

    const ai = getAllActionItems(mission).find((a) => a._id === planItem.linkedToTask)

    // Under a fixed fee the line is a cost only: the fee is the revenue
    if (isUnderFixedFee({ ai, allActions })) {
        return { ...data, estRevenue: 0, actualRevenue: 0 }
    }

    const markup = getCostPlusMarkup({ ai, allActions })

    return markup === null
        ? data
        : { ...data, estRevenue: costPlus(data.estCost, markup), actualRevenue: costPlus(data.actualCost, markup) }
}

function priceLineItem({ planItem, from, to, mission, fxm, precise }) {
    // An equipment line prices its cost by rateTime and its revenue by billUnit. Other cost
    // lines price both by rateTime (their billUnit is often junk, e.g. "Hourly").
    if (
        planItem?.type === "cost" &&
        planItem.equipmentType &&
        planItem.billUnit &&
        planItem.billUnit !== planItem.rateTime
    ) {
        const args = { from, to, mission, fxm, precise }
        const cost = priceLineItem({ ...args, planItem: { ...planItem, billUnit: planItem.rateTime } })
        const revenue = priceLineItem({ ...args, planItem: { ...planItem, rateTime: planItem.billUnit } })

        return {
            estCost: cost.estCost,
            actualCost: cost.actualCost,
            estRevenue: revenue.estRevenue,
            actualRevenue: revenue.actualRevenue,
        }
    }

    let data = {
        estCost: 0,
        estRevenue: 0,
        actualCost: 0,
        actualRevenue: 0,
    }

    const billable = isMissionBillable(mission)

    if (!fxm) {
        fxm = () => 1
    }

    if (from && to && isAfter(from, to)) {
        return data
    }

    const fxMulti = fxm(toZonedTime(planItem.endDate))

    if (planItem.type === "fundable_milestone") {
        if (
            (!from && !to) ||
            isWithinInterval(toZonedTime(planItem.endDate), {
                start: from,
                end: to,
            })
        ) {
            data.estRevenue = ensureNumber(planItem.billRate) * fxMulti
            if (planItem.approved) {
                data.actualRevenue = data.estRevenue
            }
        }

        return data
    }

    if (!from && !to && planItem.rateTime === "One time") {
        data.estCost = ensureNumber(planItem.rate) * (planItem.quantity || 1) * fxMulti
        if (billable) {
            data.estRevenue = ensureNumber(planItem.billRate) * (planItem.quantity || 1) * fxMulti
        }

        let ed = toZonedTime(planItem.endDate)
        if (planItem.linkedToTask && mission) {
            let aiEndDate = getAllActionItems(mission).find((a) => a._id === planItem?.linkedToTask)?.endDate

            if (aiEndDate) {
                aiEndDate = toZonedTime(aiEndDate)
            }

            if (aiEndDate && isAfter(ed, endOfMonth(aiEndDate))) {
                ed = endOfMonth(aiEndDate)
            }
        }

        if (isBefore(ed, endOfMonth(new Date())) || isSameDay(ed, endOfMonth(new Date()))) {
            data.actualCost =
                excludeNull(planItem?.actualCost) >= 0
                    ? ensureNumber(planItem?.actualCost)
                    : ensureNumber(planItem.rate) * (planItem.quantity || 1) * fxm(ed)

            if (billable) {
                data.actualRevenue =
                    excludeNull(planItem?.actualRevenue) >= 0
                        ? ensureNumber(planItem?.actualRevenue)
                        : ensureNumber(planItem.billRate) * (planItem.quantity || 1) * fxm(ed)
            }
        }
    } else if (planItem.rateTime === "One time") {
        const isInRange = isOneTimePlanItemInRangeCheck({ planItem, from, to, precise })

        if (isInRange) {
            data.estCost = ensureNumber(planItem.rate) * (planItem.quantity || 1) * fxMulti
            if (billable) {
                data.estRevenue = ensureNumber(planItem.billRate) * (planItem.quantity || 1) * fxMulti
            }

            if (
                isBefore(toZonedTime(planItem.endDate), endOfMonth(new Date())) ||
                isSameDay(toZonedTime(planItem.endDate), endOfMonth(new Date()))
            ) {
                if (excludeNull(planItem.actualCost) >= 0 && planItem.actualCost !== null) {
                    data.actualCost = planItem.actualCost * fxMulti
                } else {
                    data.actualCost =
                        excludeNull(planItem.actualCost) >= 0
                            ? ensureNumber(planItem?.actualCost)
                            : ensureNumber(planItem.rate) * (planItem.quantity || 1) * fxMulti
                }

                if (billable) {
                    if (planItem.actualRevenue >= 0 && planItem.actualRevenue !== null) {
                        data.actualRevenue = planItem.actualRevenue * fxMulti
                    } else {
                        data.actualRevenue = planItem?.actualRevenue
                            ? ensureNumber(planItem?.actualRevenue)
                            : ensureNumber(planItem.billRate) * (planItem.quantity || 1) * fxMulti
                    }
                }
            }
        }
    } else {
        const idv = getIntervalOfData({ planItem, from, to, mission, fxm })

        return idv
    }

    return data
}

function getChildren(array, id) {
    return array.reduce((r, obj) => {
        if (obj.parentId === id) {
            r.push(obj, ...getChildren(array, obj._id))
        }
        return r
    }, [])
}

export function getActionInsights({ action, actions, mission, timesheets = [] }) {
    let data = {
        estCost: 0,
        estRevenue: 0,
        hoursNeeded: 0,
        actualCost: 0,
        actualRevenue: 0,
        actualHours: 0,
        percentComplete: 0,
        actions: {},
    }

    const allActions = getAllActions(mission)

    if (!actions && action) {
        actions = [action]
    }

    let totalHours = 0
    let weightedHours = 0

    actions.forEach((act, i) => {
        let kids = getChildren(allActions, act._id)

        const myActions = [...[act], ...kids]

        myActions.forEach((ac, i) => {
            let actionTotalHours = 0
            let actionWeightedHours = 0

            ac.actionItems.forEach((ai, i) => {
                const aiData = getActionItemCostRevenue({ ai, mission, totals: true, timesheets })

                data.estCost += ensureNumber(aiData.estCost)
                data.estRevenue += ensureNumber(aiData.estRevenue)
                data.hoursNeeded += ensureNumber(aiData.hoursNeeded)
                data.actualCost += ensureNumber(aiData.actualCost)
                data.actualRevenue += ensureNumber(aiData.actualRevenue)
                data.actualHours += ensureNumber(aiData.actualHours)

                let hn = aiData.hoursNeeded

                if (!hn) {
                    hn =
                        eachDayOfInterval({
                            start: toZonedTime(ai.startDate),
                            end: toZonedTime(ai.endDate),
                        }).filter((d) => !isWeekend(d)).length * 8
                }

                totalHours += ensureNumber(hn)
                weightedHours += ensureNumber(ai.percentComplete) * ensureNumber(hn)

                actionTotalHours += ensureNumber(hn)
                actionWeightedHours += ensureNumber(ai.percentComplete) * ensureNumber(hn)
            })

            data.actions[ac._id] = {
                percentComplete: ensureNumber(Math.round(actionWeightedHours / actionTotalHours)),
            }
        })
    })

    data.percentComplete = totalHours ? Math.round(weightedHours / totalHours) : 0

    return data
}

function getOverlappingBusinessDaysInIntervals(interval1, interval2) {
    // Check if intervals overlap
    if (!areIntervalsOverlapping(interval1, interval2)) {
        return 0
    }

    // Find the overlapping range
    const overlapStart = max([interval1.start, interval2.start])
    const overlapEnd = min([interval1.end, interval2.end])

    // Get all days in the overlapping range
    const days = eachDayOfInterval({ start: overlapStart, end: overlapEnd })

    // Filter out weekends and count remaining days
    const businessDays = days.filter((day) => !isWeekend(day))
    return businessDays.length
}

export function getActionItemCostRevenue({
    ai,
    mission,
    totals,
    from,
    to,
    timesheets = [],
    precise,
    fxm,
    stop,
    ignoreFixed,
}) {
    let estCost = 0,
        estRevenue = 0,
        hoursNeeded = 0,
        actualCost = 0,
        actualRevenue = 0,
        actualHours = 0

    const isBillable = isMissionBillable(mission)

    const rolePlanItems = mission.planItems.filter(
        (p) => p.type === "person" && p.raciOnTasks?.find((r) => r.taskId === ai._id) && !p.wasDeleted,
    )

    if (ai.metaType === 4 && ai.billRate && ai.pinType === "fundable-milestone") {
        if (
            (!from && !to) ||
            (from &&
                to &&
                isWithinInterval(toZonedTime(ai.endDate), {
                    start: from,
                    end: to,
                }))
        ) {
            estRevenue += ensureNumber(ai.billRate)

            if (ai.percentComplete === 100) {
                actualRevenue += ensureNumber(ai.billRate)
            }
        }
    } else {
        mission.planItems.forEach((p, i) => {
            if (p.linkedToTask === ai._id && p.type === "cost") {
                const budget = getLineItemCostAndRevenue({
                    planItem: p,
                    mission,
                    from: from,
                    to: to,
                    precise,
                })

                estCost += budget.estCost
                estRevenue += budget.estRevenue
                actualCost += budget.actualCost
                actualRevenue += budget.actualRevenue
            }
        })

        rolePlanItems.forEach((pi) => {
            const d = getRaciBudgetForRole({
                mission,
                planItem: pi,
                timesheets,
                from,
                to,
                fxm,
                forTask: ai,
                ignoreFixed,
            })

            actualCost += ensureNumber(d.actualCost)

            estCost += ensureNumber(d.estCost)

            if (isBillable) {
                actualRevenue += ensureNumber(d.actualRevenue)
                estRevenue += ensureNumber(d.estRevenue)
            }

            actualHours += ensureNumber(d.actualHours)

            hoursNeeded += ensureNumber(d.hoursNeeded)
        }) //here

        //Stragglers: on a package billed cost plus, their cost plus the markup
        const taskMarkup = timesheets?.length ? getCostPlusMarkup({ ai, allActions: getAllActions(mission) }) : null
        // Under a fixed fee they earn nothing: the fee is the revenue
        const taskFixed = timesheets?.length ? isUnderFixedFee({ ai, allActions: getAllActions(mission) }) : false

        timesheets
            ?.filter((t) => {
                if (t.status !== "approved") return false
                if (from && to) {
                    return areIntervalsOverlapping(
                        {
                            start: toZonedTime(t.weekStart),
                            end: toZonedTime(t.weekEnd),
                        },
                        {
                            start: from,
                            end: to,
                        },
                    )
                }
                return true
            })
            .forEach((ts, i) => {
                let startIndex = 0
                let endIndex = 6

                if (from && to) {
                    if (isSameWeek(toZonedTime(ts.weekStart), from, { weekStartsOn: 1 })) {
                        startIndex = getISODay(from) - 1
                    }
                    if (isSameWeek(toZonedTime(ts.weekEnd), to, { weekStartsOn: 1 })) {
                        endIndex = getISODay(to) - 1
                    }
                }

                const tsa = ts.actionItems.find((tt) => {
                    return tt.actionItemId === ai._id
                })

                const idP = getTimesheetPlanItemId(ts)

                if (tsa && !rolePlanItems.some((p) => p._id === idP)) {
                    const hr = getHourlyRates(
                        ts.snapshot?.planItem || ts.planItem || mission.planItems.find((p) => p._id === idP),
                    )
                    const hrs = getTimeEntryHours(tsa, startIndex, endIndex)
                    actualHours += ensureNumber(hrs)

                    actualCost += ensureNumber(hrs * hr.rate)

                    if (isBillable && !taskFixed) {
                        actualRevenue +=
                            taskMarkup === null
                                ? ensureNumber(hrs * hr.billRate)
                                : ensureNumber(costPlus(hrs * hr.rate, taskMarkup))
                    }
                }
            })

        if (!isValidNumber(hoursNeeded)) {
            hoursNeeded = ensureNumber(
                eachDayOfInterval({
                    start: toZonedTime(ai.startDate),
                    end: toZonedTime(ai.endDate),
                }).filter((d) => {
                    return !isWeekend(d)
                }).length * 8,
            )
        }
    }
    //}

    if (!isMissionBillable(mission)) {
        estRevenue = 0
    }

    return {
        estCost,
        estRevenue,
        hoursNeeded,
        actualHours,
        actualCost: actualCost,
        actualRevenue: actualRevenue,
    }
}

export function missionFinancials({
    missions,
    mission,
    timesheets = [],
    invoices = [],
    orgData,
    from,
    to,
    exchangeRates,
    app,
    dead,
    breakAt,
    slim,
    precise,
    rebilledExpenses = [],
}) {
    let data = slim
        ? { estCost: 0, actualCost: 0, actualHours: 0, estRevenue: 0, budgetAvailable: 0 }
        : {
              estCost: 0,
              actualCost: 0,
              actualHours: 0,
              estRevenue: 0,
              invoiced: 0,
              collected: 0,
              actualRevenue: 0,
              peopleCost: 0,
              peopleRev: 0,
              peopleActualCost: 0,
              peopleActualRev: 0,
              lineItemCost: 0,
              lineItemRev: 0,
              lineItemActualCost: 0,
              lineItemActualRev: 0,
              budgetAvailable: 0,
          }

    if (dead) {
        return data
    }

    if (from && to && isAfter(from, to)) {
        return data
    }

    if (mission && !missions) {
        missions = [mission]
    }

    const tsByMission = Object.groupBy(
        timesheets
            .filter((ts) => ts.status === "approved")
            .map((ts) => {
                const missionId =
                    getObjectId(ts.missionId) || getObjectId(ts.mission) || getObjectId(ts.snapshot?.mission)

                return {
                    ...ts,
                    mission: missionId,
                }
            }),
        (ts) => ts.mission, // The callback function goes here as the second argument
    )

    missions.forEach((myMission, i) => {
        data.currency = myMission.currency || orgData?.currency

        if (!myMission.planItems) {
            return
        }

        const missionTimesheets = tsByMission?.[myMission._id] || []

        if (missionTimesheets.length) {
            data.actualHours += missionTimesheets.reduce((cum, ts) => {
                return (cum += ensureNumber(getTimesheetHours(ts)))
            }, 0)
        }

        const allActions = myMission.planItems?.reduce((cum, p) => [...cum, ...(p.actions || [])], [])
        const allAisHere = getAllActionItems(myMission)

        const isBillable = isMissionBillable(myMission) || myMission.isModel

        let useMultiplier = false

        if (exchangeRates) {
            useMultiplier = exchangeRates && myMission.currency !== orgData?.currency
        }

        const fxm = (d) => {
            if (!exchangeRates || !exchangeRates?.length) {
                return 1
            }

            if (isAfter(d, new Date())) {
                d = subDays(new Date(), 1)
            }

            if (isWeekend(d)) {
                d = nextFriday(subDays(d, 3))
            }

            const fxReturn =
                getFxForTheDay({
                    useMultiplier,
                    currency: myMission.currency,
                    exchangeRates,
                    day: d,
                    app,
                }) || 1

            return fxReturn
        }

        data.budgetAvailable += (myMission.budgetAvailable || 0) * fxm(toZonedTime(myMission.planEndDate))

        const roles = myMission.planItems.filter((p) => {
            if (!p?.type) {
                return false
            }
            if (p.type !== "person") {
                return false
            }

            //GT CRM roles are counted elsewhere - getRoleItemCostAndRevenue prices them at 0
            if (p.createdFromGtCrm) {
                return false
            }
            return true
        })

        roles.forEach((planItem, i) => {
            const roleTimesheets = missionTimesheets.filter((ts) => {
                const df = getTimesheetPlanItemId(ts)

                return df === planItem._id
            })

            let costRevenueForRole = getRoleItemCostAndRevenue({
                planItem,
                mission: myMission,
                from,
                to,
                roleTimesheets,
                fxm,
                slim,
            })

            if (costRevenueForRole) {
                data.estCost += ensureNumber(costRevenueForRole.estCost)
                data.actualCost += ensureNumber(costRevenueForRole.actualCost)

                if (!slim) {
                    data.peopleCost += ensureNumber(costRevenueForRole.estCost)
                    data.peopleActualCost += ensureNumber(costRevenueForRole.actualCost)

                    if (isBillable) data.peopleActualRev += ensureNumber(costRevenueForRole.actualRevenue)
                }

                if (isBillable) {
                    data.estRevenue += ensureNumber(costRevenueForRole.estRevenue)
                    if (!slim) data.peopleRev += ensureNumber(costRevenueForRole.estRevenue)

                    data.actualRevenue += ensureNumber(costRevenueForRole.actualRevenue)
                }
            }
        })

        const lineItems = myMission.planItems.filter((p) => p.type === "cost")

        lineItems.forEach((planItem, i) => {
            // A line linked to a task gives way to amounts fixed on its package or any above it
            const ai = allAisHere.find((a) => a._id === planItem.linkedToTask)
            const parentCosts = getAmountsSetForLinkedTask({ ai, allActions, isBillable })

            const costRevenueForLineItem = getLineItemCostAndRevenue({
                planItem,
                mission: myMission,
                from,
                to,
                fxm,
                precise,
            })

            if (!parentCosts.actualCost) {
                data.lineItemActualCost += ensureNumber(costRevenueForLineItem.actualCost)
                data.actualCost += ensureNumber(costRevenueForLineItem.actualCost)
            }
            if (!parentCosts.estCost) {
                data.estCost += ensureNumber(costRevenueForLineItem.estCost)
                data.lineItemCost += ensureNumber(costRevenueForLineItem.estCost)
            }

            if (isBillable) {
                if (!parentCosts.actualRevenue) {
                    data.actualRevenue += ensureNumber(costRevenueForLineItem.actualRevenue)
                    data.lineItemActualRev += ensureNumber(costRevenueForLineItem.actualRevenue)
                }
                if (!parentCosts.estRevenue) {
                    data.estRevenue += ensureNumber(costRevenueForLineItem.estRevenue)
                    data.lineItemRev += ensureNumber(costRevenueForLineItem.estRevenue)
                }
            }
        })

        if (myMission.billUsingMilestones) {
            let msRevenue = 0
            let msActualRev = 0
            myMission.planItems.forEach((ms, i) => {
                if (ms.type !== "fundable_milestone") {
                    return
                }

                let isInRange = false

                try {
                    isInRange =
                        !from && !to
                            ? true
                            : isWithinInterval(toZonedTime(ms.endDate), {
                                  start: from,
                                  end: to,
                              })
                } catch (e) {}

                if (isInRange) {
                    const fm = fxm(toZonedTime(ms.endDate))
                    msRevenue += ensureNumber(ms.billRate || ms.amount) * fm

                    if (ms.approved) {
                        msActualRev += ensureNumber(ms.billRate || ms.amount) * fm
                    }
                }
            })

            if (isBillable) {
                data.actualRevenue += msActualRev
                data.estRevenue += msRevenue
            }
        }

        allActions.forEach((a, i) => {
            const aendDate = startOfDay(toZonedTime(a.startDate))
            if (
                from &&
                to &&
                !isWithinInterval(aendDate, {
                    start: from,
                    end: to,
                })
            ) {
                return
            }

            if (from && !to && isBefore(aendDate, from)) {
                return
            }

            if (!from && to && isAfter(aendDate, to)) {
                return
            }

            // A package's own amount gives way to one fixed on any package above it
            const higherAmount = getAmountsSetAbove({ allActions, actionId: a._id, isBillable })

            if (a.actualCost && !higherAmount.actualCost) {
                data.actualCost += a.actualCost
            }
            if (a.estCost && !higherAmount.estCost) {
                data.estCost += a.estCost
            }
            if (a.estRevenue && isBillable && hasHandTypedRevenue(a) && !higherAmount.estRevenue) {
                data.estRevenue += a.estRevenue
            }
            // A fixed fee earns by its own rule (ready milestones, confirmed %), never a typed actual
            if (
                a.actualRevenue &&
                isBillable &&
                hasHandTypedRevenue(a) &&
                !isFixedFee(a.billingType) &&
                !higherAmount.actualRevenue
            ) {
                data.actualRevenue += a.actualRevenue
            }
        })

        // A package billed by % complete earns the fee x each confirmed step, on its "as of" date
        if (isBillable) {
            allActions.forEach((a) => {
                if (a.billingType !== BILLING.FIXED_PERCENT || getBillingLock({ actionId: a._id, allActions })) return

                data.actualRevenue += getProgressRevenue({ fee: a.estRevenue, progress: a.billingProgress, from, to })
            })

            // Approved expenses the mission rebills (getRebilledExpenses), in its currency
            rebilledExpenses.forEach((exp) => {
                if (exp.missionId && exp.missionId !== myMission._id) return
                if (from && to && !isWithinInterval(toZonedTime(exp.expenseDate), { start: from, end: to })) return

                const billed = ensureNumber(exp.billed) * fxm(toZonedTime(exp.expenseDate))

                data.estRevenue += billed
                data.actualRevenue += billed
            })
        }

        allAisHere.forEach((ai, i) => {
            if (ai.metaType === 4 && ai.pinType === "fundable-milestone" && ai.billRate && isBillable) {
                if (from && to && !isWithinInterval(toZonedTime(ai.endDate), { start: from, end: to })) {
                    return false
                }

                // By milestones they are the fee (its sum, each realized once ready); by %
                // complete they don't bill at all
                const billing = allActions.some((a) => a?.billingType) ? getTaskBilling({ ai, allActions }) : null

                if (billing?.type !== BILLING.FIXED_PERCENT) {
                    data.estRevenue += ensureNumber(ai.billRate)

                    if (ai.percentComplete === 100) {
                        data.actualRevenue += ensureNumber(ai.billRate)
                    }
                }
            }

            ai.approvals?.forEach((cr, i) => {
                if (cr.approvedOn) {
                    if (from && to && !isWithinInterval(toZonedTime(cr.approvedOn), { start: from, end: to })) {
                        return
                    } else if (from && !to && isBefore(toZonedTime(cr.approvedOn), from)) {
                        return
                    }

                    if (isBillable) {
                        data.estRevenue += ensureNumber(cr.billRate)
                    } else {
                        data.estCost += ensureNumber(cr.billRate)
                    }
                }
            })
        })
    })

    return data
}

export const getHoursNeedFromRaci = (raciObj, ai, from, to, prorateWithoutConstraints = true) => {
    // Input validation

    if (!raciObj || !ai || (!raciObj.hoursPer && !raciObj.hoursNeeded) || !ai?.startDate || !ai?.endDate) {
        return raciObj?.hoursPer || raciObj?.hoursNeeded || 0
    }

    const hoursPer = ensureNumber(raciObj.hoursPer || raciObj.hoursNeeded)
    if (hoursPer <= 0) {
        return 0
    }

    if (!raciObj.frequency || raciObj.frequency === "Total" || raciObj.frequency === "total-days") {
        if (to && from) {
            const startToUseT = toZonedTime(ai.startDate)
            const endToUseT = toZonedTime(ai.endDate)

            // If no overlap with current month, return 0
            if (isAfter(startToUseT, to) || isBefore(endToUseT, from)) return 0

            // Clip action item to its actual start/end
            const actionStart = startToUseT
            const actionEnd = endToUseT

            // Get all business days in action item
            const allBusinessDays = eachDayOfInterval({ start: actionStart, end: actionEnd }).filter(
                (d) => !isWeekend(d),
            )

            if (allBusinessDays.length === 0) return 0

            // Compute hours per day (front-loaded)
            const maxDailyHours = 8 // adjust if needed
            const totalHours = hoursPer
            const hoursPerDay = []
            let remainingHours = totalHours

            for (let i = 0; i < allBusinessDays.length; i++) {
                const h = Math.min(maxDailyHours, remainingHours)
                hoursPerDay.push({ day: allBusinessDays[i], hours: h })
                remainingHours -= h
                if (remainingHours <= 0) break
            }

            // Sum hours that fall into current month window
            const allocatedHours = hoursPerDay
                .filter((d) => d.day >= from && d.day <= to)
                .reduce((sum, d) => sum + d.hours, 0)

            return ensureNumber(Math.round(allocatedHours))
        } else {
            return hoursPer
        }
    }

    // Determine effective date range
    let startToUse, endToUse
    try {
        startToUse = raciObj.startDate ? toZonedTime(raciObj.startDate) : toZonedTime(ai.startDate)
        endToUse = raciObj.endDate ? toZonedTime(raciObj.endDate) : toZonedTime(ai.endDate)
    } catch (e) {
        // Fallback to direct Date parsing for timestamps
        startToUse = raciObj.startDate ? new Date(raciObj.startDate) : new Date(ai.startDate)
        endToUse = raciObj.endDate ? new Date(raciObj.endDate) : new Date(ai.endDate)
    }

    // Validate base dates
    if (isNaN(startToUse.getTime()) || isNaN(endToUse.getTime()) || isAfter(startToUse, endToUse)) {
        return 0
    }

    // Apply from/to constraints, handling null explicitly
    let hasFromToConstraints = false
    if (from && to) {
        let fromDate, toDate

        try {
            fromDate = typeof from === "number" ? toZonedTime(from) : new Date(from)
            toDate = typeof to === "number" ? toZonedTime(to) : new Date(to)
        } catch (e) {}
        if (isNaN(fromDate.getTime()) || isNaN(toDate.getTime()) || isAfter(fromDate, toDate)) {
            return 0
        }
        if (!areIntervalsOverlapping({ start: startToUse, end: endToUse }, { start: fromDate, end: toDate })) {
            return 0
        }
        if (isBefore(toDate, endToUse)) {
            endToUse = toDate
        }
        if (isAfter(fromDate, startToUse)) {
            startToUse = fromDate
        }
        hasFromToConstraints = true
    }

    // Calculate hours based on frequency
    let totalHours = 0
    switch (raciObj.frequency) {
        case "Per day": {
            const days = eachDayOfInterval({ start: startToUse, end: endToUse })
            const businessDays = days.filter((d) => !isWeekend(d)).length
            totalHours = businessDays * hoursPer
            break
        }
        case "days-per-week":
        case "Per week": {
            // Round to nearest 0.1 hour to avoid floating point issues
            const days = eachDayOfInterval({ start: startToUse, end: endToUse })
            const businessDays = days.filter((d) => !isWeekend(d)).length

            // Use precise decimal calculation
            totalHours = Math.round(((businessDays * hoursPer) / 5) * 10) / 10

            break
        }
        case "days-per-month":
        case "Per month": {
            const months = eachMonthOfInterval({ start: startToUse, end: endToUse })

            // Always prorate, but controlled by prorateWithoutConstraints flag
            for (const month of months) {
                const monthStart = startOfMonth(month)
                const monthEnd = endOfMonth(month)
                const rangeStart = isAfter(startToUse, monthStart) ? startToUse : monthStart
                const rangeEnd = isBefore(endToUse, monthEnd) ? endToUse : monthEnd
                const monthDays = eachDayOfInterval({ start: monthStart, end: monthEnd })
                const totalBusinessDaysInMonth = monthDays.filter((d) => !isWeekend(d)).length
                const rangeDays = eachDayOfInterval({ start: rangeStart, end: rangeEnd })
                const rangeBusinessDays = rangeDays.filter((d) => !isWeekend(d)).length
                if (totalBusinessDaysInMonth > 0) {
                    const proportion =
                        hasFromToConstraints || prorateWithoutConstraints
                            ? rangeBusinessDays / totalBusinessDaysInMonth
                            : 1 // No proration if no constraints and prorateWithoutConstraints is false
                    totalHours += proportion * hoursPer
                }
            }

            break
        }

        default:
            return 0
    }
    if (!from && !to) {
        return ensureNumber(Math.round(totalHours))
    }
    return ensureNumber(totalHours)
}

// One lookup per rates list (it's asked for every day of every role, line and timesheet)
const rateLookups = new WeakMap()

/**
 * The multiplier into the base currency on a day: 1 / the latest rate published on or before
 * it (utils/fxLookup - weekends, holidays, a missed run and today take the last rate there is).
 */
export function getFxForTheDay({ useMultiplier, exchangeRates, currency, day, app }) {
    if (!useMultiplier || !exchangeRates?.length) return 1

    if (!rateLookups.has(exchangeRates)) rateLookups.set(exchangeRates, makeRateLookup(exchangeRates))
    const rateAmount = rateLookups.get(exchangeRates)(currency, day)

    return rateAmount ? 1 / rateAmount : 1
}
